import React, { useEffect, useState } from "react";
import { Medal, DownloadSimple, CheckCircle, Copy } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Premium partner badge download + embed panel.
 * Only shown for Premium tiers. Provides SVG, PNG downloads + HTML embed snippet
 * for partner websites / email signatures.
 */
export default function BadgePanel({ slug, tier }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const isPremium = tier !== "free";

  useEffect(() => {
    if (!isPremium) return;
    api.get(`/dashboard/businesses/${slug}/badge-embed`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [slug, isPremium]);

  if (!isPremium) return null;
  if (!data) {
    return (
      <div className="border border-border rounded-sm p-5 mb-6 bg-card" data-testid="badge-panel-loading">
        <div className="text-xs text-muted-foreground">Chargement du badge…</div>
      </div>
    );
  }

  const downloadPng = async () => {
    try {
      const url = `${api.defaults.baseURL}/dashboard/businesses/${slug}/badge.png`;
      const response = await fetch(url);
      const blob = await response.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `badge-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Badge PNG téléchargé");
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const downloadSvg = async () => {
    try {
      const url = `${api.defaults.baseURL}/dashboard/businesses/${slug}/badge.svg`;
      const response = await fetch(url);
      const blob = await response.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `badge-${slug}.svg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Badge SVG téléchargé");
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(data.embed_html);
      setCopied(true);
      toast.success("Code HTML copié");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copie impossible");
    }
  };

  return (
    <div className="border border-border rounded-sm p-6 mb-6 bg-gradient-to-br from-card to-accent/10" data-testid="badge-panel">
      <div className="flex items-center gap-2 mb-4">
        <Medal size={18} weight="fill" className="text-secondary" />
        <div>
          <div className="font-display text-lg font-medium">Votre badge Partenaire</div>
          <div className="text-xs text-muted-foreground">Affichez-le sur votre devanture, votre site, vos réseaux — preuve sociale immédiate.</div>
        </div>
      </div>

      {/* Badge preview — use relative URL for live rendering across any domain */}
      <div className="flex justify-center mb-5 p-5 bg-background rounded-sm border border-border" data-testid="badge-preview">
        <img
          src={`${api.defaults.baseURL}/dashboard/businesses/${slug}/badge.svg`}
          alt="Badge Partenaire Mon Commerce 68"
          className="max-w-full h-auto"
          style={{ width: "min(500px, 100%)" }}
        />
      </div>

      {/* Download buttons */}
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <button
          onClick={downloadPng}
          className="flex items-center justify-center gap-2 border border-foreground hover:bg-foreground hover:text-background px-4 py-3 rounded-sm text-sm font-semibold transition-colors"
          data-testid="badge-download-png"
        >
          <DownloadSimple size={16} weight="bold" /> PNG (600×200 · réseaux / print)
        </button>
        <button
          onClick={downloadSvg}
          className="flex items-center justify-center gap-2 border border-foreground hover:bg-foreground hover:text-background px-4 py-3 rounded-sm text-sm font-semibold transition-colors"
          data-testid="badge-download-svg"
        >
          <DownloadSimple size={16} weight="bold" /> SVG (vectoriel · devanture)
        </button>
      </div>

      {/* Embed snippet */}
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2 block">
          Code HTML pour votre site / signature email
        </label>
        <div className="relative">
          <textarea
            readOnly
            value={data.embed_html}
            rows={5}
            className="w-full text-[11px] font-mono bg-background border border-border rounded-sm p-3 pr-11 resize-none leading-relaxed"
            data-testid="badge-embed-textarea"
            onClick={(e) => e.target.select()}
          />
          <button
            onClick={copyEmbed}
            className="absolute top-2 right-2 w-8 h-8 grid place-items-center bg-foreground text-background rounded-sm hover:bg-foreground/90"
            title="Copier"
            data-testid="badge-copy-embed"
          >
            {copied ? <CheckCircle size={14} weight="fill" /> : <Copy size={14} />}
          </button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
          💡 Astuce : collez ce code dans le footer de votre site ou dans la signature de vos emails Outlook/Gmail pour rendre votre partenariat visible à chaque interaction.
        </div>
      </div>
    </div>
  );
}
